import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Add as AddIcon, Edit as EditIcon, LinkOff as LinkOffIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { publishersService, titlesService, Title } from '../services/firebaseFunctions';
import { usePublisherLinkTitlesModal } from '../hooks/usePublisherLinkTitlesModal';
import PublisherLinkTitlesModal from '../components/PublisherLinkTitlesModal';
import { useAuth } from '../contexts/AuthContext';

const PublisherDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('editoras', 'editar');
  const { id } = useParams<{ id: string }>();
  const [publisherName, setPublisherName] = useState<string | null>(null);
  const [titles, setTitles] = useState<Title[]>([]);
  const [allTitles, setAllTitles] = useState<Title[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [unlinkingTitleId, setUnlinkingTitleId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [publishersData, linkedTitles, catalog] = await Promise.all([
        publishersService.getAll(),
        publishersService.getTitlesByPublisherId(id),
        titlesService.getAll(),
      ]);
      const publisher = publishersData.find((p) => p.id === id);
      setPublisherName(publisher ? publisher.name : null);
      setTitles(linkedTitles);
      setAllTitles(catalog);
      if (!publisher) {
        setSnackbar({ open: true, message: 'Editora não encontrada', severity: 'error' });
      }
    } catch (error) {
      console.error('Error loading publisher detail:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar dados', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const linkModal = usePublisherLinkTitlesModal(id, allTitles, {
    onAfterSync: load,
    onNotifySuccess: (message) => setSnackbar({ open: true, message, severity: 'success' }),
    onNotifyError: (message) => setSnackbar({ open: true, message, severity: 'error' }),
  });

  const handleUnlinkTitle = async (titleId: string) => {
    if (!id) return;
    if (!window.confirm('Remover o vínculo deste livro com esta editora?')) return;
    try {
      setUnlinkingTitleId(titleId);
      const res = await publishersService.clearTitlePublisherLink(titleId, id);
      if (!res.success) throw new Error(res.error || 'Falha ao remover vínculo');
      setSnackbar({
        open: true,
        message: res.data?.changed === false ? 'Livro já estava sem esta editora.' : res.message || 'Vínculo removido.',
        severity: 'success',
      });
      await load();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Erro ao remover vínculo',
        severity: 'error',
      });
    } finally {
      setUnlinkingTitleId(null);
    }
  };

  const openNameModal = () => {
    setNameDraft(publisherName || '');
    setNameModalOpen(true);
  };

  const handleSaveName = async () => {
    if (!id) return;
    const trimmed = nameDraft.trim();
    if (trimmed.length < 2) {
      setSnackbar({ open: true, message: 'Nome deve ter pelo menos 2 caracteres.', severity: 'error' });
      return;
    }
    try {
      setSavingName(true);
      const res = await publishersService.update(id, { name: trimmed });
      if (!res.success) throw new Error(res.error || 'Erro ao atualizar');
      setPublisherName(trimmed);
      setSnackbar({ open: true, message: 'Nome da editora atualizado.', severity: 'success' });
      setNameModalOpen(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Erro ao salvar nome',
        severity: 'error',
      });
    } finally {
      setSavingName(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/publishers')}>
          Voltar
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {publisherName ?? 'Editora'}
          </Typography>
          <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Editoras.'}>
            <span>
              <IconButton color="primary" onClick={openNameModal} aria-label="Editar nome da editora" size="small" disabled={!canManage}>
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Editoras.'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => linkModal.openModal(titles)} disabled={!canManage}>
              Vincular livros
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        {titles.length} {titles.length === 1 ? 'livro' : 'livros'} nesta editora
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Capa</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Autores</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {titles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhum livro vinculado a esta editora.
                </TableCell>
              </TableRow>
            ) : (
              titles.map((title) => (
                <TableRow key={title.id}>
                  <TableCell>
                    {title.coverUrl ? (
                      <Box
                        component="img"
                        src={title.coverUrl}
                        alt=""
                        sx={{ width: 40, height: 56, objectFit: 'cover', borderRadius: 1 }}
                      />
                    ) : (
                      <Box sx={{ width: 40, height: 56, bgcolor: 'action.hover', borderRadius: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{title.name}</TableCell>
                  <TableCell>{title.type === 'audiobook' ? 'Audiobook' : 'E-book'}</TableCell>
                  <TableCell>{(title.authors || []).join(', ') || '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Editoras.'}>
                      <span>
                        <IconButton
                          size="small"
                          color="warning"
                          aria-label="Remover vínculo com a editora"
                          disabled={!canManage || unlinkingTitleId === title.id}
                          onClick={() => handleUnlinkTitle(title.id)}
                        >
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PublisherLinkTitlesModal
        open={linkModal.open}
        saving={linkModal.saving}
        linkItems={linkModal.linkItems}
        onClose={linkModal.closeModal}
        titlesForSelectRow={linkModal.titlesForSelectRow}
        onAddItem={linkModal.handleAddLinkItem}
        onRemoveItem={linkModal.handleRemoveLinkItem}
        onChangeItem={linkModal.handleLinkItemChange}
        onMoveUp={linkModal.handleMoveLinkUp}
        onMoveDown={linkModal.handleMoveLinkDown}
        onSave={linkModal.handleSave}
      />

      <Dialog open={nameModalOpen} onClose={() => !savingName && setNameModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar nome da editora</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameModalOpen(false)} disabled={savingName}>
            Cancelar
          </Button>
          <Button onClick={handleSaveName} variant="contained" disabled={savingName}>
            {savingName ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublisherDetailPage;
